import * as React from "react";
import { Link } from "react-router";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * A link in running text or under a paragraph. Appearance is `.text-link`.
 *
 * Not a `Button variant="link"`: these are links, they navigate, and half of
 * them sit mid-sentence. A control styled as text is a different thing from text
 * that is a control, and conflating them is how a form ends up with a "Cancel"
 * that is a link and a "Cancel" that is a button on two adjacent screens.
 *
 * **`external` sets `rel="noreferrer noopener"`, and that is the reason it is a
 * prop rather than a convention.** A bare `target="_blank"` hands the opened page
 * a live `window.opener` reference back to ours, which it can navigate. It was
 * spelled out correctly at all four external links here - but "remembered
 * correctly four times" is not a guarantee, and the fifth is the one that ships.
 *
 * It also appends the outward arrow, because the affordance and the target
 * should never disagree.
 */
export function TextLink({
  to,
  href,
  external = false,
  tone = "accent",
  children,
  className,
}: {
  /** Internal route. Mutually exclusive with `href`. */
  to?: string;
  /** External URL. Mutually exclusive with `to`. */
  href?: string;
  external?: boolean;
  tone?: "accent" | "subtle";
  children: React.ReactNode;
  className?: string;
}) {
  const classes = cn("text-link", `text-link--${tone}`, className);

  if (to) {
    return (
      <Link to={to} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer noopener" : undefined}
      className={classes}
    >
      {children}
      {external && <ArrowUpRight />}
    </a>
  );
}
